import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  TrendingUp, Target, FileText, Files, Sparkles, Calendar,
  Zap, Bell, User, CreditCard, HelpCircle, LogOut, Menu, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared in-app navigation shell (2026-08-25 design-system pass).
// One sidebar and one mobile top bar for every logged-in page, extracted from
// the Dashboard sidebar so the workspace looks the same wherever you are.
// This is deliberately NOT the marketing SiteHeader: logged-in pages get the
// sidebar, public pages get the marketing nav.

const primaryNav = [
  { to: '/dashboard', label: 'Dashboard', icon: TrendingUp, testId: 'nav-dashboard' },
  { to: '/wins', label: 'All Wins', icon: TrendingUp, testId: 'nav-wins' },
  { to: '/goals', label: 'Goals', icon: Target, testId: 'nav-goals' },
  { to: '/brag-doc/new', label: 'Generate Doc', icon: FileText, testId: 'nav-brag-doc' },
  { to: '/documents', label: 'My Reports', icon: Files, testId: 'nav-documents' },
  { to: '/coach', label: 'Career Coach', icon: Sparkles, testId: 'nav-coach' },
  { to: '/weekly-review', label: 'Weekly Review', icon: Calendar, testId: 'nav-weekly-review' },
];

const secondaryNav = [
  { to: '/profile', label: 'Profile', icon: User, testId: 'nav-profile' },
  { to: '/subscription', label: 'Subscription', icon: CreditCard, testId: 'nav-subscription' },
  { to: '/contact', label: 'Contact Support', icon: HelpCircle, testId: 'nav-contact' },
];

const AppShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) return;
      const response = await axios.get(`${API}/notifications`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (error) {
      // Notifications are non-critical; the nav still works without them.
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/auth/logout`,
        {},
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      navigate('/login');
    }
  };

  const isActive = (to) => {
    if (to === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/app';
    }
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const linkClass = (to) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
      isActive(to) ? '' : 'text-gray-700 hover:bg-navy-50'
    }`;

  const linkStyle = (to) =>
    isActive(to) ? { backgroundColor: 'var(--navy-100)', color: 'var(--navy-900)' } : {};

  const NavLinks = ({ onNavigate }) => (
    <>
      <nav className="space-y-2">
        {primaryNav.map(({ to, label, icon: Icon, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className={linkClass(to)}
            style={linkStyle(to)}
            onClick={onNavigate}
          >
            <Icon className="w-5 h-5" />
            {label}
          </Link>
        ))}
      </nav>

      <div className="mt-8 pt-8 border-t border-gray-200 space-y-2">
        <Link
          to="/pricing"
          data-testid="nav-pricing"
          className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
          style={{ backgroundColor: 'var(--cream-100)', color: 'var(--navy-900)' }}
          onClick={onNavigate}
        >
          <Zap className="w-5 h-5" />
          Upgrade
        </Link>

        <button
          onClick={() => { setShowNotifications(true); if (onNavigate) onNavigate(); }}
          className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-700 hover:bg-navy-50 transition-colors duration-200 w-full relative"
        >
          <Bell className="w-5 h-5" />
          Notifications
          {unreadCount > 0 && (
            <span className="absolute right-4 top-3 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold" style={{ backgroundColor: '#EF4444', color: 'white' }}>
              {unreadCount}
            </span>
          )}
        </button>

        {secondaryNav.map(({ to, label, icon: Icon, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className={linkClass(to)}
            style={linkStyle(to)}
            onClick={onNavigate}
          >
            <Icon className="w-5 h-5" />
            {label}
          </Link>
        ))}

        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 w-full"
        >
          <LogOut className="w-5 h-5" />
          Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden md:block w-64 shrink-0 border-r border-gray-200 bg-white">
        <div className="p-6 sticky top-0">
          <Link to="/dashboard" className="block mb-8 transition-opacity hover:opacity-80" aria-label="BragBox dashboard">
            <img src="/assets/bragbox-hero.png" alt="BragBox" className="h-10" />
          </Link>
          <NavLinks />
        </div>
      </div>

      {/* Mobile full-screen menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto md:hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-100">
            <img src="/assets/bragbox-hero.png" alt="BragBox" className="h-8" />
            <button onClick={() => setShowMobileMenu(false)} className="p-2" aria-label="Close menu">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6">
            <NavLinks onNavigate={() => setShowMobileMenu(false)} />
          </div>
        </div>
      )}

      {/* Content column */}
      <div className="flex-1 overflow-auto" style={{ backgroundColor: 'var(--navy-50)' }}>
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-40">
          <Link to="/dashboard" aria-label="BragBox dashboard">
            <img src="/assets/bragbox-hero.png" alt="BragBox" className="h-8" />
          </Link>
          <button onClick={() => setShowMobileMenu(true)} className="p-2" aria-label="Open menu">
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {children}
      </div>

      {/* Notifications */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Notifications
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {notifications.length === 0 ? (
              <p className="text-center py-12 text-gray-500">No notifications yet</p>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                    notif.read ? 'border-gray-200 bg-gray-50' : 'border-navy-200'
                  }`}
                  onClick={() => markAsRead(notif.notification_id)}
                  style={{ backgroundColor: notif.read ? '#F9FAFB' : 'var(--navy-50)' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--navy-900)' }}>
                      {notif.title}
                    </h4>
                    <span className="text-xs text-gray-500">
                      {new Date(notif.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed break-words">
                    {notif.message}
                  </p>
                  {!notif.read && (
                    <p className="text-xs mt-2" style={{ color: 'var(--navy-600)' }}>
                      Select to mark as read
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppShell;
